/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import CustomerModel from 'cio/models/customer';
import ProfileShortcutModel, { RenderedProfileShortcut } from 'cio/models/profile-shortcut';
import { hash, task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import ApiService from 'cio/services/api';
import { IdentifierKey } from 'cio/utils/identifiers';
import { action } from '@ember/object';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import EventBusService, { CUSTOMER_ATTRIBUTES_UPDATED } from 'cio/services/event-bus';
import { Header } from 'cio/utils/models/header';

interface ProfileShortcutArgs {
  customer: CustomerModel;
}

type SendWebhookShortcutResponse = {
  headers?: Header[];
  response_body?: string;
  render_errors?: string[];
  status?: number;
};

type ProcessedSendWebhookShortcutResponse = {
  status?: number;
  headers?: Header[];
  response_body?: string;
  render_errors?: string[];
};

export const MAX_SHORTCUTS_COUNT = 20;

export default class ProfileShortcuts extends Component<ProfileShortcutArgs> {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service eventBus!: EventBusService;
  @service store!: DS.Store;

  @tracked isShowingShortcutAddModal = false;
  @tracked isShowingShortcutEditModal = false;
  @tracked shortcutIdToEdit: string | null = null;
  @tracked isShowingShortcutDeleteModal = false;
  @tracked shortcutIdToDelete: string | null = null;

  @tracked renderedShortcuts: RenderedProfileShortcut[] = [];
  @tracked shortcuts: ProfileShortcutModel[] = [];

  @tracked isShowingSendWebhookShortcutModal = false;
  @tracked webhookShortcutToSend: RenderedProfileShortcut | null = null;
  @tracked lastSendWebhookShortcutResult: null | ProcessedSendWebhookShortcutResponse = null;

  get sortedRenderedShortcuts() {
    let shortcutMap = this.shortcuts.reduce<Record<string, ProfileShortcutModel>>((acc, shortcut) => {
      acc[shortcut.id] = shortcut;
      return acc;
    }, {});

    return this.renderedShortcuts
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }))
      .map((b) => ({
        rendered: b,
        original: shortcutMap[b.id],
      }));
  }

  get shouldShowAddShortcuts() {
    return this.renderedShortcuts.length > 0 && this.renderedShortcuts.length < MAX_SHORTCUTS_COUNT;
  }

  get shouldShowNoShortcutDropdown() {
    return this.renderedShortcuts.length === 0 && !this.isShowingShortcutAddModal;
  }

  get canAddShortcuts() {
    return this.auth.hasPermission('journeys/people/edit');
  }

  @action
  didInsert() {
    this.fetchShortcuts.perform();
    this.eventBus.subscribe(CUSTOMER_ATTRIBUTES_UPDATED, this, this.fetchShortcuts.perform);
  }

  willDestroy() {
    super.willDestroy();
    this.eventBus.unsubscribe(CUSTOMER_ATTRIBUTES_UPDATED, this, this.fetchShortcuts.perform);
  }

  fetchShortcuts = task({ drop: true }, async () => {
    try {
      let result = await hash({
        rendered: this.args.customer.getRenderedShortcuts({}),
        original: this.store.findAll('profile-shortcut'),
      });

      this.renderedShortcuts = result.rendered;
      this.shortcuts = result.original.toArray();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  sendWebhookShortcutTask = task({ drop: true }, async (shortcut: RenderedProfileShortcut) => {
    this.webhookShortcutToSend = shortcut;
    this.isShowingSendWebhookShortcutModal = true;

    try {
      let result = (await this.api.post(`profile_shortcuts/${shortcut.id}/send`, {
        ignoreErrors: true,
        data: {
          customer_id: this.args.customer.identifiers[IdentifierKey.CioId],
        },
      })) as SendWebhookShortcutResponse;

      this.lastSendWebhookShortcutResult = {
        ...result,
      };
    } catch (e) {
      if (e.render_errors != null) {
        this.lastSendWebhookShortcutResult = e;
      } else {
        this.apiErrors.report(e);
        throw e;
      }
    }
  });

  @action
  showShortcutAddModal() {
    this.isShowingShortcutAddModal = true;

    this.analytics.track('shortcuts', 'create_started');
  }

  @action
  showShortcutEditModal(shortcutId: string) {
    this.shortcutIdToEdit = shortcutId;
    this.isShowingShortcutEditModal = true;
    this.analytics.track('shortcuts', 'edit_started');
  }

  @action
  showShortcutDeleteModal(shortcutId: string) {
    this.shortcutIdToDelete = shortcutId;
    this.isShowingShortcutDeleteModal = true;
    this.analytics.track('shortcuts', 'delete_started');
  }

  @action
  closeShortcutEditModal() {
    this.isShowingShortcutEditModal = false;
    this.shortcutIdToEdit = null;
  }

  @action
  closeShortcutDeleteModal() {
    this.isShowingShortcutDeleteModal = false;
    this.shortcutIdToDelete = null;
  }

  @action
  trackShortcutsOpened() {
    this.analytics.track('shortcuts', 'opened');
  }

  @action
  closeSendWebhookShortcutModal() {
    this.isShowingSendWebhookShortcutModal = false;
    this.lastSendWebhookShortcutResult = null;
  }
}
